import { useCallback, useRef } from "react";
import { IconPaperclip } from "@/assets/icons/geist/IconPaperclip";
import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { cn } from "@/components/podkit/lib/cn";
import { type ImageAttachment } from "@/components/ai/prompt-input/state";
import { processImageFiles } from "@/components/ai/prompt-input/use-image-drop";

export type { ImageAttachment };

const ALLOWED_TYPES = ["image/png", "image/jpeg"];

type AddImageButtonProps = {
    onImagesSelected: (images: ImageAttachment[]) => void;
    disabled?: boolean;
    className?: string;
};

type ImageChipProps = {
    image: ImageAttachment;
    onRemove: (id: string) => void;
};

/**
 * Button component for adding images - renders just the paperclip icon
 */
export function AddImageButton({ onImagesSelected, disabled, className }: AddImageButtonProps) {
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = useCallback(
        async (files: FileList | null) => {
            const images = await processImageFiles(files, toast);
            if (images.length > 0) {
                onImagesSelected(images);
            }
        },
        [onImagesSelected, toast],
    );

    const handleClick = useCallback(() => {
        inputRef.current?.click();
    }, []);

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_TYPES.join(",")}
                multiple
                onChange={(e) => {
                    void handleFiles(e.target.files);
                    // Reset input so same file can be selected again
                    e.target.value = "";
                }}
                className="hidden"
                disabled={disabled}
            />
            <button
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className={cn(
                    "flex items-center justify-center rounded-lg p-2 text-content-secondary hover:bg-surface-tertiary hover:text-content-primary disabled:cursor-not-allowed disabled:opacity-50",
                    className,
                )}
                aria-label="Add image"
                data-tracking-id="add-image-prompt-input"
            >
                <IconPaperclip size="sm" />
            </button>
        </>
    );
}

/**
 * Chip component for displaying an attached image thumbnail
 */
export function ImageChip({ image, onRemove }: ImageChipProps) {
    const handleRemove = useCallback(() => {
        // Revoke the blob URL when explicitly removing the image
        URL.revokeObjectURL(image.preview);
        onRemove(image.id);
    }, [image.id, image.preview, onRemove]);

    return (
        <div
            className="flex w-min max-w-full items-center space-x-2 rounded-md border border-border-subtle bg-surface-primary px-2 py-1"
            data-testid="image-context-chip"
        >
            <img
                src={image.preview}
                alt={image.name}
                className="size-4 shrink-0 rounded object-cover"
            />
            <span className="truncate text-sm font-medium">{image.name}</span>
            <button
                className="rounded-full text-content-muted hover:text-content-accent"
                onClick={handleRemove}
                data-tracking-id="delete-image-context-prompt-input"
            >
                <span className="sr-only">Delete &quot;{image.name}&quot; image</span>
                <IconCircleX className="size-4.5 shrink-0" />
            </button>
        </div>
    );
}
